import dayjs, { Dayjs } from 'dayjs';
import { useScheduleActionLock } from '../../../hook/useScheduleActionLock';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, concatDateAndTimeDayjs, isSameDate, Row } from '../../../lib/utils';
import { Body3Regular, Subtitle1Bold } from '../../../lib/font';
import { colors } from '../../../lib/colors';
import ColorSquareButton from '../../Button/ColorSquareButton';
import SvgIcon from '../../SvgIcon';
import CreateEtcScheduleLeft from './CreateEtcScheduleLeft';
import CreateEtcScheduleRight from './CreateEtcScheduleRight';
import Calendar from '../../Calendar/Calendar';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    createGeneralScheduleAPI,
    createRepeatGeneralScheduleAPI,
    RepeatGeneralScheduleRecurrence,
} from '../../../api/schedule';

type RefineDate = {
    refineStartTime: string;
    refineEndTime: string;
};

type RepeatPayload = {
    recurrence: RepeatGeneralScheduleRecurrence;
    /** 성공 후 캘린더를 이동시킬 첫 회차 시각 (ISO) */
    anchorStartTime: string;
};

/** 'none' = 반복 안 함 / 'everyWeek' = 매주 반복 / 'everyDay' = 매일 반복 */
export type EtcScheduleRepeatType = 'none' | 'everyWeek' | 'everyDay';

/**
 * 반복 횟수 상한은 frequency 마다 다르다.
 * 서버(rappo `src/utils/onetimeScheduleRecurrence.ts`)가 쓰는 식과 같다.
 *   maxCount = min(MAX_RECURRENCE_COUNT(200), floor(MAX_RECURRENCE_HORIZON_DAYS(400) / step) + 1)
 *   - DAILY(step 1)  : min(200, 401) = 200  ← 개수 상한이 먼저 걸린다
 *   - WEEKLY(step 7) : min(200, 58)  = 58   ← horizon 이 먼저 걸린다
 * 매주 반복은 59회부터 400을 받으므로, 서버에 보내기 전에 프론트에서 먼저 막는다.
 */
const MAX_REPEAT_COUNT_EVERY_DAY = 200;
const MAX_REPEAT_COUNT_EVERY_WEEK = 58;

const getMaxRepeatCount = (repeatType: EtcScheduleRepeatType): number =>
    repeatType === 'everyWeek' ? MAX_REPEAT_COUNT_EVERY_WEEK : MAX_REPEAT_COUNT_EVERY_DAY;

const getRepeatTypeLabel = (repeatType: EtcScheduleRepeatType): string =>
    repeatType === 'everyWeek' ? '매주' : '매일';

/** 상한 안내 한 줄. 다른 문장 뒤에 이어 붙일 때 쓴다(줄바꿈이 없어야 괄호가 갈라지지 않는다). */
const buildRepeatLimitLine = (repeatType: EtcScheduleRepeatType): string =>
    `${getRepeatTypeLabel(repeatType)} 반복은 한 번에 최대 ${getMaxRepeatCount(
        repeatType
    )}회까지 등록할 수 있습니다`;

const buildRepeatLimitMessage = (repeatType: EtcScheduleRepeatType): string =>
    `${buildRepeatLimitLine(repeatType)}\n횟수를 나눠서 등록해주세요`;

/** 겹침 안내에 날짜를 몇 개까지 나열할지. 서버는 conflicts 를 최대 5건 준다. */
const MAX_SHOWN_CONFLICT_DATES = 3;

const REPEAT_CONFLICT_HEADLINE = '반복 회차 중 겹치는 일정이 있어 하나도 등록되지 않았습니다';

/** 겹침 응답을 다른 400 계열 에러(RECURRENCE_COUNT_EXCEEDED 등)와 구분하는 코드. */
const RECURRENCE_CONFLICT_ERROR_CODE = 'RECURRENCE_CONFLICT';

/**
 * 410/420/430 응답의 겹침 상세를 사람이 읽는 문장으로 만든다.
 *
 * details 는 백엔드 배포 상태에 따라 아예 없거나 형태가 다를 수 있으므로,
 * 값이 온전할 때만 쓰고 아니면 빈 문자열을 돌려 호출부가 폴백 문구를 쓰게 한다.
 *
 * conflicts[].conflictType(SCHEDULE / ONETIME_SCHEDULE / GROUP_SCHEDULE / REQUEST / NEW_OPPORTUNITY)
 * 은 일부러 노출하지 않는다. 종류가 섞이면 alert 한 줄에 담기지 않아 날짜가 오히려 안 읽힌다.
 */
const buildConflictDetailMessage = (res: any): string => {
    // errorCode 는 body 최상위로도, details 안으로도 올 수 있어 둘 다 본다.
    const errorCode = res?.data?.errorCode ?? res?.data?.details?.errorCode;
    if (errorCode !== RECURRENCE_CONFLICT_ERROR_CODE) return '';

    const details = res?.data?.details;
    // conflicts 는 이른 회차 순으로 최대 5건 온다. 정렬은 서버 순서를 그대로 믿는다.
    const conflicts = Array.isArray(details?.conflicts) ? details.conflicts : [];

    const conflictDates = conflicts
        .map((conflict: any) => dayjs(conflict?.startTime))
        .filter((date: Dayjs) => date.isValid())
        .map((date: Dayjs) => date.format('M월 D일'));

    if (conflictDates.length === 0) return '';

    const shownDates = conflictDates.slice(0, MAX_SHOWN_CONFLICT_DATES);
    const reportedCount = Number(details?.conflictCount);
    // conflicts 는 최대 5건만 오므로 총 개수는 conflictCount 를 우선한다.
    const totalCount =
        Number.isFinite(reportedCount) && reportedCount > conflictDates.length
            ? reportedCount
            : conflictDates.length;
    const restCount = totalCount - shownDates.length;

    // 겹침 사유는 headline 이 이미 말하므로 여기서는 날짜만 나열한다.
    // 문장까지 붙이면 최악(3건 + 외 N건)에 alert 본문 폭(456px)을 넘겨 임의 위치에서 줄바꿈된다.
    return restCount > 0
        ? `겹친 날짜 : ${shownDates.join(', ')} 외 ${restCount}건`
        : `겹친 날짜 : ${shownDates.join(', ')}`;
};

/**
 * 반복 등록 멱등키(operationId) 생성.
 * features/sharedMembership/accounting.ts 의 방식과 동일하게 crypto.randomUUID 를 우선 사용하고,
 * 없는 브라우저에서는 서버가 uuid 형식을 검증할 수 있으므로 v4 형태를 맞춰 만든다.
 */
const createOperationId = (): string => {
    const randomUUID = globalThis.crypto?.randomUUID?.bind(globalThis.crypto);
    if (randomUUID) return randomUUID();

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
        const random = (Math.random() * 16) | 0;
        const value = character === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
};

const CreateEtcSchedule = ({
    coachOption,
    authorization,
    authObject,
    createAlert,
    selectedSchedule,
    calendarType,
    calendarSelectedDay,
    calendarScrollY,
}: any) => {
    /** 스케줄 동작 잠금(생성) — 진입은 경로 가드가, 저장 직전 재검사(30분 만료)는 여기서 한다 */
    const scheduleLock = useScheduleActionLock('schedule.create');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const submissionLockRef = useRef<boolean>(false);
    const currentTime = selectedSchedule ? dayjs(selectedSchedule?.scheduleStartTime) : dayjs();
    const roundedTime = currentTime.minute(Math.floor(currentTime.minute() / 10) * 10).second(0);
    const currentTimeAdd50 = dayjs(roundedTime).add(50, 'minute');

    /**
     * title : 일정 제목
     * coach : 담당강사
     * startTime : 시작 시간
     * endTime : 종료시간
     * selectedDay : 날짜
     * repeatType : 반복 방식 (반복 안 함 / 매주 반복 / 매일 반복)
     * repeatCount : 최초 일정을 포함한 총 반복 횟수
     */
    const [title, setTitle] = useState<string>('');
    const [coach, setCoach] = useState({
        value: selectedSchedule ? selectedSchedule?.coachId : '',
        label: selectedSchedule ? selectedSchedule?.coachName : '강사를 선택해주세요!',
    });

    const [startTime, setStartTime] = useState({ value: roundedTime, label: dayjs(roundedTime).format('A hh:mm') });
    const [endTime, setEndTime] = useState({
        value: currentTimeAdd50,
        label: dayjs(currentTimeAdd50).format('A hh:mm'),
    });
    const [timeList, setTimeList] = useState<any>(null); // 10분단위 시간 배열

    const [repeatType, setRepeatType] = useState<EtcScheduleRepeatType>('none');
    const [repeatCount, setRepeatCount] = useState<string>('');

    /**
     * 멱등키는 화면 진입 시 1회만 만든다.
     * 제출할 때마다 새로 만들면 응답 유실 뒤 재시도했을 때 일정이 두 벌 생성될 수 있다.
     */
    const [operationId] = useState<string>(() => createOperationId());

    /** 캘린더 state */
    const [selectedDay, setSelectedDay] = useState<any>(
        selectedSchedule ? new Date(selectedSchedule?.scheduleStartTime) : new Date()
    );
    const [isCalendar, setIsCalendar] = useState<boolean>(false);

    const handleChangeTitle = (e: any) => {
        setTitle(e.target.value);
    };

    // 담당 강사 선택
    const handleCoachSelect = (option: any) => {
        setCoach(option);
    };
    // 시작 시간 선택
    const handleStartTimeSelect = (option: any) => {
        setStartTime(option);
    };

    // 종료 시간 선택
    const handleEndTimeSelect = (option: any) => {
        setEndTime(option);
    };

    // 반복 주기 변경. 매일 → 매주로 바꾸면 상한이 201 → 58 로 줄어드는데,
    // 이미 넣어둔 횟수가 새 상한을 넘으면 조용히 서버 400 을 맞게 되므로 상한으로 맞춰준다.
    const handleChangeRepeatType = (nextRepeatType: EtcScheduleRepeatType) => {
        setRepeatType(nextRepeatType);

        if (nextRepeatType === 'none') return;

        const nextMaxRepeatCount = getMaxRepeatCount(nextRepeatType);
        const currentCount = Number(repeatCount);

        if (Number.isInteger(currentCount) && currentCount > nextMaxRepeatCount) {
            setRepeatCount(String(nextMaxRepeatCount));
            // 조용히 바꾸면 사용자가 200회를 등록한 줄 안다. 조정 사실을 알린다.
            createAlert(
                '반복 횟수 조정',
                `${getRepeatTypeLabel(nextRepeatType)} 반복은 최대 ${nextMaxRepeatCount}회까지 등록할 수 있어\n반복 횟수를 ${nextMaxRepeatCount}회로 조정했습니다`,
                false
            );
        }
    };

    // 반복 횟수 입력 (숫자만 허용)
    const onChangeRepeatCount = (value: string) => {
        const onlyNumber = value.replace(/[^0-9]/g, '');
        setRepeatCount(onlyNumber);
    };

    // 시간추가
    const onClickAddTime = (addNumber: number) => {
        if (
            dayjs(startTime.value).format('YYYY-MM-DD') ===
            dayjs(endTime.value).add(addNumber, 'minute').format('YYYY-MM-DD')
        ) {
            setEndTime({
                value: dayjs(endTime.value).add(addNumber, 'minute'),
                label: dayjs(dayjs(endTime.value).add(addNumber, 'minute')).format('A hh:mm'),
            });
        } else {
            createAlert('입력 오류', '추가 가능한 시간을 초과하였습니다', '확인', () => {});
        }
    };

    // 시간빼기
    const onClickSubTime = (subNumber: number) => {
        if (dayjs(startTime.value).isBefore(dayjs(endTime.value).subtract(subNumber, 'minute'))) {
            setEndTime({
                value: dayjs(endTime.value).subtract(subNumber, 'minute'),
                label: dayjs(dayjs(endTime.value).subtract(subNumber, 'minute')).format('A hh:mm'),
            });
        } else {
            createAlert('입력 오류', '감소 가능한 시간을 초과하였습니다', '확인', () => {});
        }
    };

    /**
     * 반복 일정 미리보기.
     *
     * ⚠️ 이 계산은 "표시 전용"이다. 실제로 어떤 날짜에 일정이 생기는지는 서버가 KST 기준으로 recurrence 를
     * 전개해서 결정한다. 프론트는 scheduleTimes 를 만들어 보내지 않으며, 여기 계산과 서버 결과가 다르면
     * 서버 결과가 정답이다. (중복/기간 초과 등으로 서버가 거절할 수도 있다)
     *
     * 기준일은 달력에서 고른 날짜의 'YYYY-MM-DD' 문자열이다. instant 로 바꿔 타임존을 거치면
     * 브라우저 타임존이 KST가 아닐 때 화면에서 고른 날짜와 다른 날짜가 보인다.
     */
    const repeatPreviewDates = useMemo<Dayjs[]>(() => {
        if (repeatType === 'none') return [];

        const count = Number(repeatCount);
        if (!Number.isInteger(count) || count < 1) return [];

        const cappedCount = Math.min(count, getMaxRepeatCount(repeatType));
        const baseDate = dayjs(dayjs(selectedDay).format('YYYY-MM-DD'));
        if (!baseDate.isValid()) return [];

        const unit = repeatType === 'everyWeek' ? 'week' : 'day';

        return Array.from({ length: cappedCount }, (_, index) => baseDate.add(index, unit));
    }, [repeatType, repeatCount, selectedDay]);

    // 4종(일간/주간/스마트/리스트) 캘린더 쿼리를 모두 무효화한다. 삭제 경로(OffEtcDetailModal)와 동일하게 맞춘다.
    const invalidateScheduleQueries = () => {
        queryClient.invalidateQueries({ queryKey: ['getDailyScheduleAPI'] });
        queryClient.invalidateQueries({ queryKey: ['getWeeklyScheduleAPI'] });
        queryClient.invalidateQueries({ queryKey: ['getSmartRangeSchedulesAPI'] });
        queryClient.invalidateQueries({ queryKey: ['getListRangeSchedulesAPI'] });
    };

    // 일반 일정 생성 API : (POST)
    const createGeneralSchedule = useMutation({
        mutationFn: async (refineDate: RefineDate) =>
            await createGeneralScheduleAPI(
                authObject?.centerId,
                coach.value,
                title,
                refineDate?.refineStartTime,
                refineDate?.refineEndTime,
                authorization
            ),
        onSuccess: (res, refineDate) => {
            if (res?.status === 201) {
                invalidateScheduleQueries();
                const startTime = refineDate.refineStartTime;
                navigate('/schedule', {
                    state: { calendarType, calendarTimeInfo: startTime },
                    replace: true,
                });
            } else if (res?.status === 410 || res?.status === 420 || res?.status === 430) {
                createAlert(
                    '중복',
                    `선택된 담당 강사 ${coach?.label}님이\n해당시간에 중복된 스케줄이 존재합니다`,
                    true
                );
            } else if (res?.status === 0) {
                createAlert('오류', '요청에 실패했습니다\n네트워크 상태를 확인한 뒤 다시 시도해주세요', true);
            } else {
                createAlert('오류', `서버 내부 오류\n 지속적인 오류발생시 고객센터로 문의바랍니다`, true);
            }
        },
        onError: () => {
            createAlert('오류', '요청에 실패했습니다\n네트워크 상태를 확인한 뒤 다시 시도해주세요', true);
        },
        onSettled: () => {
            submissionLockRef.current = false;
        },
    });

    // 일반 일정 반복 생성 API : (POST)
    const createRepeatGeneralSchedule = useMutation({
        mutationFn: async (payload: RepeatPayload) =>
            await createRepeatGeneralScheduleAPI(
                authObject?.centerId,
                coach.value,
                title,
                operationId,
                payload.recurrence,
                authorization
            ),
        onSuccess: (res, payload) => {
            if (res?.status === 201) {
                invalidateScheduleQueries();

                const moveToScheduleCalendar = () =>
                    navigate('/schedule', {
                        state: { calendarType, calendarTimeInfo: payload.anchorStartTime },
                        replace: true,
                    });

                // 일간 뷰에는 그날 1건만 보여서 실패로 오해하기 쉽다. 총 몇 건이 만들어졌는지 먼저 알린다.
                // count 는 서버 배포 상태에 따라 없을 수 있으므로 있을 때만 쓴다.
                const registeredCount = Number(res?.data?.count);
                if (Number.isFinite(registeredCount) && registeredCount > 0) {
                    createAlert(
                        '등록 완료',
                        `총 ${registeredCount}건의 일반일정이 등록되었습니다\n캘린더에서는 날짜별로 나뉘어 표시됩니다`,
                        false,
                        '확인',
                        moveToScheduleCalendar
                    );
                } else {
                    moveToScheduleCalendar();
                }
            } else if (res?.status === 410 || res?.status === 420 || res?.status === 430) {
                // 서버는 한 회차라도 겹치면 전체를 등록하지 않는다. 부분 등록이 아님을 분명히 한다.
                const conflictDetail = buildConflictDetailMessage(res);
                createAlert(
                    '중복',
                    conflictDetail
                        ? `${REPEAT_CONFLICT_HEADLINE}\n${conflictDetail}`
                        : `${REPEAT_CONFLICT_HEADLINE}\n담당 강사 ${coach?.label}님의 기존 일정을 확인해주세요`,
                    true
                );
            } else if (res?.status === 400) {
                // 400은 시간/규칙 오류, 개수 초과, 기간 초과, 배치 내부 겹침이 모두 섞여 온다.
                createAlert(
                    '입력 오류',
                    `반복 일정을 등록할 수 없습니다\n반복 횟수와 시간을 다시 확인해주세요\n${buildRepeatLimitLine(
                        repeatType
                    )}`,
                    true
                );
            } else if (res?.status === 404) {
                // 강사 미발견과 기능 비활성(킬스위치 OFF)이 모두 404 로 오고 프론트가 구분할 방법이 없다.
                // 어느 쪽이든 사용자가 재시도로 해결할 수 없으므로 재시도를 단정적으로 권하지 않는다.
                createAlert('오류', '요청을 처리할 수 없습니다\n잠시 후 다시 시도하거나 관리자에게 문의해주세요', true);
            } else if (res?.status === 403 || res?.status === 406) {
                // 403: 다른 센터 admin 이거나 읽기 전용(centerViewer) 계정 / 406: admin·superAdmin 이 아닌 role
                createAlert('권한 오류', '일정을 등록할 권한이 없습니다\n계정 권한을 확인해주세요', true);
            } else if (res?.status === 0) {
                createAlert('오류', '요청에 실패했습니다\n네트워크 상태를 확인한 뒤 다시 시도해주세요', true);
            } else {
                createAlert('오류', `서버 내부 오류\n 지속적인 오류발생시 고객센터로 문의바랍니다`, true);
            }
        },
        onError: () => {
            createAlert('오류', '요청에 실패했습니다\n네트워크 상태를 확인한 뒤 다시 시도해주세요', true);
        },
        onSettled: () => {
            submissionLockRef.current = false;
        },
    });

    const isSubmitting = createGeneralSchedule.isLoading || createRepeatGeneralSchedule.isLoading;

    // 일반일정 등록 버튼 클릭이벤트
    const onClickCreateSchedule = () => {
        // 중복 클릭 차단: isLoading 이 리렌더로 반영되기 전 연타를 ref 락으로 먼저 막는다.
        if (submissionLockRef.current || createGeneralSchedule.isLoading || createRepeatGeneralSchedule.isLoading) {
            return;
        }

        if (coach?.value === '') {
            createAlert('필수 입력', '담당 강사를 선택해주세요', true);
            return;
        }

        if (title.trim() === '') {
            createAlert('필수 입력', '일정 제목을 입력해주세요', true);
            return;
        }

        const concatStartTime = dayjs(concatDateAndTimeDayjs(selectedDay, startTime.value));
        const concatEndTime = dayjs(concatDateAndTimeDayjs(selectedDay, endTime.value));

        const refineStartTime = dayjs(concatStartTime).toISOString();
        const refineEndTime =
            dayjs(concatEndTime).format('HH:mm') === '00:00'
                ? dayjs(concatEndTime).add(1, 'day').toISOString()
                : dayjs(concatEndTime).toISOString();
        if (isSameDate(dayjs(refineStartTime), dayjs(refineEndTime))) {
            createAlert('입력 오류', '시작 시간과 완료 시간이 같습니다!\n다시 한번 확인해주세요!', '확인', () => {});
            return;
        }

        if (dayjs(refineStartTime).isAfter(dayjs(refineEndTime))) {
            createAlert(
                '입력 오류',
                '시작 시간이 완료 시간보다 늦을 수  없습니다!\n다시 한번 확인해주세요!',
                '확인',
                () => {}
            );
            return;
        }

        // 반복 안 함 : 기존 단건 등록 API 를 그대로 사용한다.
        if (repeatType === 'none') {
            const refineDate: RefineDate = {
                refineStartTime: refineStartTime,
                refineEndTime: refineEndTime,
            };

            // 저장 직전 스케줄 동작 잠금 재검사(30분 만료) — 통과해야 보낸다
            scheduleLock.run(() => {
                if (submissionLockRef.current) return;
                submissionLockRef.current = true;
                createGeneralSchedule.mutate(refineDate);
            });
            return;
        }

        // 반복 등록 : 새 반복 등록 API 를 사용한다.
        const count = Number(repeatCount);

        if (repeatCount.trim() === '' || !Number.isInteger(count) || count < 1) {
            createAlert('필수 입력', '반복 횟수를 1회 이상 입력해주세요', true);
            return;
        }

        if (count > getMaxRepeatCount(repeatType)) {
            createAlert('입력 오류', buildRepeatLimitMessage(repeatType), true);
            return;
        }

        /**
         * recurrence 는 KST 벽시계 값이다. 사용자가 화면에서 고른 날짜·시각을 그대로 문자열로 조립한다.
         *
         * instant(Date/ISO)로 바꿨다가 되돌리면 브라우저 타임존이 KST가 아닐 때 값이 밀린다.
         * (예: 뉴욕 브라우저에서 18:00 을 고르면 익일 07:00 KST 가 전송된다)
         * 날짜 전개는 서버가 하고, endTime 이 '00:00' 이면 서버가 익일 00:00 으로 해석하므로
         * 프론트에서 날짜를 +1일 해서 보내지 않는다. (localDate 는 언제나 화면에서 고른 시작일)
         */
        const recurrenceLocalDate = dayjs(selectedDay).format('YYYY-MM-DD');
        const recurrenceStartTime = dayjs(startTime.value).format('HH:mm');
        const recurrenceEndTime = dayjs(endTime.value).format('HH:mm');

        // 서버 규칙: endTime === '00:00' 은 익일 00:00(정상), 그 외에 endTime <= startTime 이면 400.
        if (recurrenceEndTime !== '00:00' && recurrenceEndTime <= recurrenceStartTime) {
            createAlert(
                '입력 오류',
                '완료 시간이 시작 시간보다 늦어야 합니다!\n다시 한번 확인해주세요!',
                true
            );
            return;
        }

        const payload: RepeatPayload = {
            recurrence: {
                frequency: repeatType === 'everyWeek' ? 'WEEKLY' : 'DAILY',
                count: count,
                localDate: recurrenceLocalDate,
                startTime: recurrenceStartTime,
                endTime: recurrenceEndTime,
            },
            anchorStartTime: refineStartTime,
        };

        scheduleLock.run(() => {
            if (submissionLockRef.current) return;
            submissionLockRef.current = true;
            createRepeatGeneralSchedule.mutate(payload);
        });
    };

    // 시작 시간이 변할때 endTime정제
    useEffect(() => {
        if (startTime) {
            const add50Min: Dayjs = dayjs(startTime.value).add(50, 'minute');

            if (
                add50Min.isAfter(dayjs(startTime.value).endOf('day')) ||
                isSameDate(add50Min, dayjs(startTime.value).endOf('day'))
            ) {
                setEndTime({
                    value: dayjs().endOf('day').add(1, 'millisecond'),
                    label: dayjs().endOf('day').add(1, 'millisecond').format('A hh:mm'),
                });
            } else {
                setEndTime({ value: dayjs(add50Min), label: dayjs(add50Min).format('A hh:mm') });
            }
        }
    }, [startTime]);

    // 시간 10분단위로 생성
    useEffect(() => {
        // 시간생성
        const generateTimeSlots = () => {
            const timeSlots = [];
            const start = dayjs().startOf('day').hour(0).minute(0);
            for (let i = 0; i < 144; i++) {
                // 24 hours * 6 (10분 간격)
                const time = start.add(i * 10, 'minute');
                const formattedTime = time.format('A hh:mm'); // 오전/오후 표시
                timeSlots.push({ value: time, label: formattedTime });
            }
            return timeSlots;
        };

        const timeSlots = generateTimeSlots();
        setTimeList(timeSlots);
    }, []);

    return (
        <Col style={{ paddingLeft: 101, paddingTop: 56, paddingRight: 101, paddingBottom: 64 }}>
            {/* 스케줄 동작 잠금 — 저장 직전 재검사 카드(body 포털) */}
            {scheduleLock.gate}
            <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Row style={{ alignItems: 'center' }}>
                    <div
                        style={{ cursor: 'pointer', marginRight: 16, display: 'flex', alignItems: 'center' }}
                        onClick={() => {
                            navigate('/schedule', {
                                state: { calendarType, calendarSelectedDay, calendarScrollY },
                                replace: true,
                            });
                        }}
                    >
                        <SvgIcon name={'SvgLeftStrokeArrow'} size={20} stroke={colors.black} fill={colors.trans} />
                    </div>
                    <Subtitle1Bold style={{ color: colors.LAVEL_4 }}>{'일반일정 등록하기'}</Subtitle1Bold>
                    <Body3Regular style={{ color: colors.LAVEL_1, marginLeft: 16 }}>
                        {'이 시간에는 회원님이 수업신청을 할 수 없어요!'}
                    </Body3Regular>
                </Row>

                <ColorSquareButton
                    isGradient={true}
                    customStyle={{ width: 136, height: 36 }}
                    text={isSubmitting ? '등록 중...' : '일반일정 등록'}
                    size="small"
                    isDisabled={isSubmitting}
                    callBack={onClickCreateSchedule}
                />
            </Row>

            <Row style={{ marginTop: 56 }}>
                <CreateEtcScheduleLeft
                    coachOption={coachOption}
                    coach={coach}
                    handleCoachSelect={handleCoachSelect}
                    title={title}
                    setTitle={setTitle}
                    handleChangeTitle={handleChangeTitle}
                />
                <CreateEtcScheduleRight
                    coachOption={coachOption}
                    coach={coach}
                    handleCoachSelect={handleCoachSelect}
                    selectedDay={selectedDay}
                    setIsCalendar={setIsCalendar}
                    handleEndTimeSelect={handleEndTimeSelect}
                    handleStartTimeSelect={handleStartTimeSelect}
                    startTime={startTime}
                    endTime={endTime}
                    timeList={timeList}
                    onClickAddTime={onClickAddTime}
                    onClickSubTime={onClickSubTime}
                    repeatType={repeatType}
                    setRepeatType={handleChangeRepeatType}
                    repeatCount={repeatCount}
                    onChangeRepeatCount={onChangeRepeatCount}
                    repeatPreviewDates={repeatPreviewDates}
                    maxRepeatCount={getMaxRepeatCount(repeatType)}
                    repeatTypeLabel={getRepeatTypeLabel(repeatType)}
                />
            </Row>

            {isCalendar && (
                <Calendar
                    selectedDay={selectedDay}
                    setSelectedDay={setSelectedDay}
                    setIsCalendar={setIsCalendar}
                    isPrevMonth={true}
                    isNextMonth={true}
                    style={{
                        width: 420,
                        height: 370,
                        borderRadius: 24,
                        backgroundColor: colors.WHITE_50,
                        boxShadow: '1px 1px 8px 0px #2B529D2E',
                        position: 'absolute',
                        zIndex: 1999,
                        marginTop: 250,
                        marginLeft: 500,
                    }}
                />
            )}
        </Col>
    );
};

export default CreateEtcSchedule;
